import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import api, { API_ORIGIN } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_BASE = `${API_ORIGIN}/api`;

export default function EstimatePrintPage() {
  const [sp] = useSearchParams();
  const id = sp.get('id');
  const isPreview = sp.get('preview') === '1';
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fitOne, setFitOne] = useState(false);
  // 셀 너비 조정: 이 화면에서만 쓰는 일회성 값 (저장되지 않음, 새로고침하면 설정값으로 복귀). null = 저장된 설정 사용
  const [colWidths, setColWidths] = useState(null);
  const [colPanel, setColPanel] = useState(null);   // null = 닫힘, { loading, error, tableWidth, columns, inputs }
  const pdfQs = (preview = isPreview) => [preview && 'preview=1', fitOne && 'fit_one_page=1', colWidths && `col_widths=${encodeURIComponent(JSON.stringify(colWidths))}`].filter(Boolean).join('&');
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // 미리보기 창에서 바로 발행: 임시저장 상태의 내 견적서일 때만 [발행] 버튼 노출
  const { user } = useAuth();
  const { canPerformAction } = usePermission();
  const [estInfo, setEstInfo] = useState(null);   // { status, business_type, created_by }
  const [publishing, setPublishing] = useState(false);
  useEffect(() => {
    if (!id) return;
    api.get(`/estimates/${id}`)
      .then(e => setEstInfo({ status: e.status, business_type: e.business_type, created_by: e.created_by }))
      .catch(() => setEstInfo(null));
  }, [id]);
  const canPublish = isPreview && !!estInfo && estInfo.status === 'draft' && !!user && estInfo.created_by === user.id
    && canPerformAction(estInfo.business_type === 'manpower' ? '서비스견적서 생성·수정' : '제품 견적서 생성·수정');

  const handlePublish = async () => {
    if (!window.confirm('발행하시겠습니까?\n\n• 작성 화면에서 저장하지 않은 변경 내용은 발행에 포함되지 않습니다.\n• 발행 후에는 수정할 수 없습니다. (발행 후 설정된 시간 이내에는 수정 가능)')) return;
    setPublishing(true);
    try {
      let r;
      try {
        r = await api.post(`/estimates/${id}/publish`, {});
      } catch (e) {
        // 공헌이익율이 조직 승인 기준에 미달하면 승인 사유가 필요
        if (!String(e.message).includes('승인 사유')) throw e;
        const reason = window.prompt('공헌이익율이 조직 승인 기준에 미달하여 승인 요청이 필요합니다.\n승인 사유를 입력하세요.');
        if (!reason || !reason.trim()) { setPublishing(false); return; }
        r = await api.post(`/estimates/${id}/publish`, { approval_reason: reason.trim() });
      }
      const listPath = estInfo.business_type === 'manpower' ? '/service-estimate' : '/product-estimate';
      if (r.status === 'pending_approval') {
        // 승인 요청: PDF 화면은 필요 없으므로 미리보기 창을 닫고 작성 화면은 목록으로 이동
        alert('승인 요청이 전송되었습니다.');
        if (window.opener && !window.opener.closed) { window.opener.location.href = listPath; window.close(); }
        else window.location.href = listPath;
        return;
      }
      // 바로 발행: 목록으로 가지 않고, 작성 화면의 [PDF 저장]을 눌렀을 때와 같은 PDF 화면(워터마크 없음)으로 전환
      alert(`발행 완료! 견적번호: ${r.estimate_number}`);
      if (window.opener && !window.opener.closed) { try { window.opener.location.reload(); } catch (e3) { /* 다른 출처면 무시 */ } } // 작성 화면은 발행된 상태로 새로고침
      window.location.replace(`/estimates/print?id=${id}`);
    } catch (e) {
      alert(e.message);
      setPublishing(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    const token = localStorage.getItem('token');
    let revokeUrl = null;
    const qs = pdfQs();
    const url = `${API_BASE}/estimates/${id}/pdf${qs ? `?${qs}` : ''}`;
    setLoading(true);
    setError(null);
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('PDF 생성에 실패했습니다.');
        return res.blob();
      })
      .then(blob => {
        revokeUrl = URL.createObjectURL(blob);
        setPdfUrl(revokeUrl);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });

    return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
  }, [id, isPreview, fitOne, colWidths]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const update = () => setContainerWidth(containerRef.current?.clientWidth || 0);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [pdfUrl]);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => setNumPages(n), []);

  const handleDownload = async () => {
    try {
      const qs = pdfQs();
      await api.download(`/estimates/${id}/pdf${qs ? `?${qs}` : ''}`, 'estimate.pdf');
    } catch (e) {
      alert(e?.message || 'PDF 저장에 실패했습니다.');
    }
  };

  const canAdjustCols = !!estInfo && estInfo.business_type !== 'manpower'; // 제품 견적서만
  const openColPanel = async () => {
    setColPanel({ loading: true });
    try {
      const r = await api.get(`/estimates/${id}/pdf-columns?${pdfQs()}`);
      setColPanel({ loading: false, tableWidth: r.tableWidth, columns: r.columns, inputs: Object.fromEntries(r.columns.map(c => [c.key, String(c.width)])) });
    } catch (e) { setColPanel({ loading: false, error: e.message || '열 너비를 불러오지 못했습니다.' }); }
  };
  const applyColPanel = () => {
    const out = {};
    for (const c of colPanel.columns) {
      const raw = String(colPanel.inputs[c.key] ?? '').trim();
      const n = raw === '' ? c.width : Number(raw); // 비워 두면 현재 폭 유지
      if (!Number.isFinite(n) || n < 20 || n > 800) { setColPanel({ ...colPanel, error: `'${c.label}' 너비는 20~800 사이의 숫자(px)로 입력하세요.` }); return; }
      out[c.key] = Math.round(n);
    }
    setColPanel(null);
    setColWidths(out);
  };

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#e8e8e8',fontFamily:'Pretendard,-apple-system,sans-serif'}}>
      <div style={{textAlign:'center',color:'#64748b'}}>
        <div style={{fontSize:24,marginBottom:12}}>PDF 생성 중...</div>
        <div style={{fontSize:13}}>잠시만 기다려 주세요.</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#e8e8e8',fontFamily:'Pretendard,-apple-system,sans-serif'}}>
      <div style={{textAlign:'center',color:'#ef4444'}}>
        <div style={{fontSize:18,marginBottom:12}}>{error}</div>
        <button onClick={() => window.history.back()} style={{padding:'8px 20px',border:'1px solid #d1d5db',background:'#fff',borderRadius:6,fontSize:12,cursor:'pointer'}}>돌아가기</button>
      </div>
    </div>
  );

  const pageWidth = containerWidth ? Math.min(containerWidth - 24, 900) : 0;

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100vh',position:'relative',background:'#e8e8e8',fontFamily:'Pretendard,-apple-system,sans-serif'}}>
      {/* 툴바 */}
      <div style={{display:'flex',gap:8,justifyContent:'center',padding:'10px 0',background:'#f8fafc',borderBottom:'1px solid #e2e8f0'}}>
        <button onClick={handleDownload} style={{padding:'8px 20px',border:'1px solid #3b82f6',background:'#3b82f6',color:'#fff',borderRadius:6,fontSize:12,cursor:'pointer',fontWeight:600}}>&#128424; PDF 저장</button>
        <button
          onClick={() => { setColPanel(null); setFitOne(v => !v); }}
          title="긴 견적을 좌우/상하 여백이 늘어나더라도 A4 한 장에 축소해 담습니다."
          style={{
            padding:'8px 16px',
            border:`1px solid ${fitOne ? '#3b82f6' : '#d1d5db'}`,
            background: fitOne ? '#eff6ff' : '#fff',
            color: fitOne ? '#1e40af' : '#475569',
            borderRadius:6, fontSize:12, cursor:'pointer', fontWeight:600,
          }}
        >&#128208; 한 장에 맞추기 {fitOne ? 'ON' : 'OFF'}</button>
        {canAdjustCols && <button onClick={() => (colPanel ? setColPanel(null) : openColPanel())} title="PDF 표의 열 너비를 이번 PDF에만 임시로 조정합니다 (저장되지 않음)"
          style={{padding:'8px 16px',border:`1px solid ${colWidths || colPanel ? '#3b82f6' : '#d1d5db'}`,background: colWidths || colPanel ? '#eff6ff' : '#fff',color: colWidths || colPanel ? '#1e40af' : '#475569',borderRadius:6,fontSize:12,cursor:'pointer',fontWeight:600}}>&#8596; 셀 너비 조정{colWidths ? ' (적용 중)' : ''}</button>}
        {canPublish && <button onClick={handlePublish} disabled={publishing} title="이 미리보기 내용 그대로 견적서를 발행합니다" style={{padding:'8px 20px',border:'1px solid #16a34a',background:'#16a34a',color:'#fff',borderRadius:6,fontSize:12,cursor:publishing?'default':'pointer',fontWeight:600,opacity:publishing?0.7:1}}>{publishing ? '발행 중...' : '📤 발행'}</button>}
        <button onClick={() => { if (window.opener && !window.opener.closed) window.close(); else window.history.back(); }} style={{padding:'8px 20px',border:'1px solid #d1d5db',background:'#fff',borderRadius:6,fontSize:12,cursor:'pointer',fontWeight:600}}>닫기</button>
        {isPreview && <span style={{display:'flex',alignItems:'center',fontSize:12,color:'#f59e0b',fontWeight:600,marginLeft:8}}>미리보기 (워터마크 포함)</span>}
      </div>

      {colPanel && (
        <div style={{position:'absolute',top:56,left:'50%',transform:'translateX(-50%)',zIndex:20,width:360,maxWidth:'94vw',background:'#fff',border:'1px solid #cbd5e1',borderRadius:10,boxShadow:'0 8px 28px rgba(0,0,0,0.18)',padding:16}}>
          <div style={{fontSize:14,fontWeight:800,marginBottom:4}}>셀 너비 조정</div>
          <div style={{fontSize:11,color:'#64748b',marginBottom:10}}>이번 PDF에만 적용되며 저장되지 않습니다. 이 화면을 새로고침하거나 닫으면 설정값으로 돌아갑니다.</div>
          {colPanel.loading ? <div style={{padding:'20px 0',textAlign:'center',color:'#64748b',fontSize:12}}>현재 너비를 불러오는 중...</div> : !colPanel.columns ? (
            <div style={{color:'#dc2626',fontSize:12,padding:'8px 0'}}>{colPanel.error}</div>
          ) : (() => {
            const sum = colPanel.columns.reduce((a, c) => a + (Number(colPanel.inputs[c.key]) || 0), 0);
            const diff = colPanel.tableWidth - sum;   // + 남음, - 초과
            const over = diff < -1;
            // 남거나 모자란 너비를 해당 칸에 더해(또는 빼서) 합계가 표 폭과 맞도록 함 (20~800px 범위 안에서)
            const fillRemainder = (key) => {
              const cur = Number(colPanel.inputs[key]) || 0;
              const next = Math.max(20, Math.min(800, cur + diff));
              setColPanel({ ...colPanel, error: null, inputs: { ...colPanel.inputs, [key]: String(Math.round(next)) } });
            };
            return (<>
              {colPanel.columns.map(c => (
                <div key={c.key} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                  <label style={{flex:1,fontSize:12,fontWeight:600,color:'#334155'}}>{c.label}</label>
                  <button type="button" disabled={Math.abs(diff) < 1} onClick={() => fillRemainder(c.key)}
                    title={Math.abs(diff) < 1 ? '합계가 표 폭과 같습니다.' : diff > 0 ? `남는 너비 ${Math.round(diff)}px를 이 칸에 더합니다` : `초과한 너비 ${Math.round(-diff)}px를 이 칸에서 뺍니다`}
                    style={{width:26,height:26,padding:0,border:'1px solid #cbd5e1',background:Math.abs(diff) < 1 ? '#f8fafc' : '#eff6ff',color:Math.abs(diff) < 1 ? '#cbd5e1' : '#1d4ed8',borderRadius:6,fontSize:13,cursor:Math.abs(diff) < 1 ? 'default' : 'pointer'}}>&#8596;</button>
                  <input type="number" min="20" max="800" step="1" value={colPanel.inputs[c.key]}
                    onChange={e => setColPanel({ ...colPanel, error: null, inputs: { ...colPanel.inputs, [c.key]: e.target.value } })}
                    onKeyDown={e => e.key === 'Enter' && applyColPanel()}
                    style={{width:90,padding:'5px 8px',border:'1px solid #cbd5e1',borderRadius:6,fontSize:12,textAlign:'right'}} />
                  <span style={{fontSize:11,color:'#94a3b8',width:16}}>px</span>
                </div>
              ))}
              <div style={{fontSize:11,marginTop:8,color: '#64748b'}}>합계 {sum}px / 표 폭 {colPanel.tableWidth}px</div>
              <div style={{fontSize:12,fontWeight:700,marginTop:2,color: over ? '#dc2626' : Math.abs(diff) < 1 ? '#16a34a' : '#1d4ed8'}}>
                {Math.abs(diff) < 1 ? '✔ 표 폭과 딱 맞습니다' : over ? `⚠ ${Math.round(-diff)}px 초과 — 오른쪽이 잘리거나 한 장에 맞추기 시 더 축소될 수 있습니다` : `${Math.round(diff)}px 남음 — 남는 폭은 열에 비례해 분배됩니다`}
              </div>
              <div style={{fontSize:10,color:'#94a3b8',marginTop:2}}>각 칸 왼쪽 ↔ 버튼: 남거나 모자란 너비를 그 칸에 반영</div>
              {colPanel.error && <div style={{color:'#dc2626',fontSize:12,marginTop:6}}>{colPanel.error}</div>}
              <div style={{display:'flex',gap:6,marginTop:12,justifyContent:'flex-end'}}>
                {colWidths && <button onClick={() => { setColPanel(null); setColWidths(null); }} style={{padding:'6px 12px',border:'1px solid #d1d5db',background:'#fff',borderRadius:6,fontSize:12,cursor:'pointer',marginRight:'auto'}}>설정값으로 되돌리기</button>}
                <button onClick={() => setColPanel(null)} style={{padding:'6px 12px',border:'1px solid #d1d5db',background:'#fff',borderRadius:6,fontSize:12,cursor:'pointer'}}>닫기</button>
                <button onClick={applyColPanel} style={{padding:'6px 16px',border:'1px solid #3b82f6',background:'#3b82f6',color:'#fff',borderRadius:6,fontSize:12,cursor:'pointer',fontWeight:600}}>적용</button>
              </div>
            </>);
          })()}
        </div>
      )}

      {/* PDF 뷰어 */}
      <div ref={containerRef} style={{flex:1,overflow:'auto',padding:12,display:'flex',flexDirection:'column',alignItems:'center',gap:12}}>
        {pdfUrl && pageWidth > 0 && (
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(e) => setError('PDF를 표시할 수 없습니다.')}
            loading={<div style={{color:'#64748b',fontSize:13,padding:20}}>PDF 로드 중...</div>}
            error={<div style={{color:'#ef4444',fontSize:13,padding:20}}>PDF를 표시할 수 없습니다.</div>}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <div key={`page-${i + 1}`} style={{boxShadow:'0 2px 8px rgba(0,0,0,0.1)',background:'#fff'}}>
                <Page
                  pageNumber={i + 1}
                  width={pageWidth}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              </div>
            ))}
          </Document>
        )}
      </div>
    </div>
  );
}
